import { Row, Col } from 'antd';
import { DonorStatus, RetiredReasons } from 'gql/types';
import { FormItem, ControlledTextArea } from 'modules/common/components';
import { DonorStatusSelect } from 'modules/donor/components';
import { getDonorStatusesAllowedToAutoRetire } from 'modules/donor/utils/get-status-rules';
import { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { getTranslation } from 'utils/getTranslation';

import { DonorStatusField } from './fields';

import DonorRetiredReasonSelect from '../../DonorRetiredReasonSelect';

const t = getTranslation('donor.EditDonorForm.DonorStatusSection');

type PropTypes = {
  fieldNamePrefix: string;
  disabled?: boolean;
  shouldRetire?: boolean;
  canChangeDonorStatus?: boolean;
};

const DonorStatusSection: FC<PropTypes> = ({
  fieldNamePrefix,
  disabled,
  shouldRetire,
  canChangeDonorStatus,
}) => {
  const { control, watch, setValue } = useFormContext();

  const getFieldName = (fieldName: DonorStatusField) => {
    return `${fieldNamePrefix}.${fieldName}`;
  };
  const donorStatus = watch(getFieldName(DonorStatusField.status));
  const retiredReason = watch(getFieldName(DonorStatusField.RetiredReason));

  const isRetired = donorStatus === DonorStatus.Retired;

  const canEditStatus = disabled;

  useEffect(() => {
    if (!isRetired) {
      setValue(getFieldName(DonorStatusField.RetiredReason), null);
      setValue(getFieldName(DonorStatusField.RetiredClarification), null);
    }
  }, [donorStatus]);

  const excludedStatuses = shouldRetire
    ? getDonorStatusesAllowedToAutoRetire()
    : [];

  return (
    <Row>
      <Col span={24}>
        {canChangeDonorStatus && (
          <FormItem label={t('status')}>
            <DonorStatusSelect
              controlled={{
                control,
                name: getFieldName(DonorStatusField.status),
              }}
              exclude={excludedStatuses}
              disabled={canEditStatus}
            />
          </FormItem>
        )}
      </Col>
      <Col span={24}>
        {isRetired && (
          <FormItem required={isRetired} label={t('retirementReason')}>
            <DonorRetiredReasonSelect
              controlled={{
                control,
                name: getFieldName(DonorStatusField.RetiredReason),
              }}
              disabled={canEditStatus}
            />
          </FormItem>
        )}
        {isRetired && retiredReason === RetiredReasons.Other && (
          <FormItem label={t('retiredClarification')}>
            <ControlledTextArea
              maxLength={250}
              disabled={canEditStatus}
              controlled={{
                control,
                name: getFieldName(DonorStatusField.RetiredClarification),
              }}
            />
          </FormItem>
        )}
      </Col>
    </Row>
  );
};

export default DonorStatusSection;
